// Auth context — wrapper runt PocketBase authStore som ger en
// React-vänlig view av user + login/register/logout/changePassword.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { pb } from '../lib/pocketbase';

export interface AuthUser {
  id: string;
  email: string;
  emailVisibility: boolean;
  verified: boolean;
  name?: string;
  avatar?: string;
  created: string;
  updated: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  updateEmail: (newEmail: string, oldPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toAuthUser(record: any): AuthUser {
  return {
    id: record.id,
    email: record.email,
    emailVisibility: record.emailVisibility ?? true,
    verified: record.verified ?? false,
    name: record.name,
    avatar: record.avatar,
    created: record.created,
    updated: record.updated,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Vid mount: kontrollera om vi har en giltig token, och förnya den om möjligt
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (pb.authStore.isValid) {
        try {
          // authRefresh returnerar ny token + uppdaterad user-data
          await pb.collection('users').authRefresh();
        } catch {
          // Token ogiltig/utgången — rensa och visa login
          pb.authStore.clear();
        }
      }
      if (!cancelled) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const record = pb.authStore.model as any;
        setUser(record ? toAuthUser(record) : null);
        setIsLoading(false);
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const authData = await pb
      .collection('users')
      .authWithPassword(email, password);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setUser(toAuthUser(authData.record as any));
  }, []);

  const register = useCallback(
    async (email: string, password: string) => {
      await pb.collection('users').create({
        email,
        password,
        passwordConfirm: password,
        emailVisibility: false,
      });
      // Auto-login efter registrering
      const authData = await pb
        .collection('users')
        .authWithPassword(email, password);
      setUser(toAuthUser(authData.record));
    },
    [],
  );

  const logout = useCallback(() => {
    pb.authStore.clear();
    setUser(null);
  }, []);

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      if (!user) throw new Error('Inte inloggad');
      await pb.collection('users').update(user.id, {
        oldPassword,
        password: newPassword,
        passwordConfirm: newPassword,
      });
    },
    [user],
  );

  const updateEmail = useCallback(
    async (newEmail: string, oldPassword: string) => {
      if (!user) throw new Error('Inte inloggad');
      await pb.collection('users').update(user.id, {
        email: newEmail,
        oldPassword,
      });
      // Uppdatera local state
      setUser((prev) => (prev ? { ...prev, email: newEmail } : prev));
    },
    [user],
  );

  const deleteAccount = useCallback(async () => {
    if (!user) throw new Error('Inte inloggad');
    await pb.collection('users').delete(user.id);
    pb.authStore.clear();
    setUser(null);
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      login,
      register,
      logout,
      changePassword,
      updateEmail,
      deleteAccount,
    }),
    [user, isLoading, login, register, logout, changePassword, updateEmail, deleteAccount],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth måste användas inom AuthProvider');
  return ctx;
}
